import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { getUserInfo } from "../../../api/axios";
import {
  APPRAISAL_STATUS,
  appraisalStatusLabel,
  getAppraisalDetail,
  getMyAppraisalHistory,
  recommendAppraisalPayIncrease,
  saveAppraisalReview,
  saveAppraisalRetroReview,
  submitManagerAppraisal,
  submitMeetingAppraisal,
  submitSelfAppraisal,
} from "./appraisalApi";
import {
  clearAppraisalAssessmentId,
  getAppraisalAssessmentId,
  setAppraisalAssessmentId,
} from "./appraisalAssessmentStorage";
import {
  AppraisalHelpModal,
  AppraisalSectionHeader,
} from "./AppraisalHelp";
import {
  buildRatingGuide,
  canAccessMyAppraisal,
  getAppraisalRoleId,
  getRatingLabel,
  normalizeRatingLabels,
} from "./appraisalAccess";
import { APPRAISAL_LIMITS, clampLen } from "./appraisalValidation";
import { AppraisalCharCount } from "./AppraisalFormFields";
import { useAppraisalFeatures } from "./appraisalFeatures";
import "./Appraisal.css";

const getRoleId = () => getAppraisalRoleId();

const getUserId = () => {
  const userInfo = getUserInfo();
  if (!userInfo) return "";
  return (
    userInfo.selfUserId ||
    userInfo.id ||
    userInfo.userId ||
    userInfo.Id ||
    userInfo.UserId ||
    ""
  );
};

const normalizeReview = (r) => ({
  id: r.id ?? r.Id,
  // API DTO uses Behaviour* (British); UI state uses Behavior*
  appraisalBehaviorId:
    r.appraisalBehaviourId ??
    r.AppraisalBehaviourId ??
    r.appraisalBehaviorId ??
    r.AppraisalBehaviorId,
  behaviorStatement:
    r.behaviourStatement ?? r.BehaviourStatement ?? r.behaviorStatement ?? r.BehaviorStatement ?? "",
  behaviorIntroduction:
    r.behaviourIntroduction ??
    r.BehaviourIntroduction ??
    r.behaviorIntroduction ??
    r.BehaviorIntroduction ??
    "",
  behaviorHelpText:
    r.behaviourHelpText ?? r.BehaviourHelpText ?? r.behaviorHelpText ?? r.BehaviorHelpText ?? "",
  valueName: r.valueName ?? r.ValueName ?? "",
  sortOrder: Number(r.sortOrder ?? r.SortOrder ?? 0),
  userRating: Number(r.userRating ?? r.UserRating ?? 0),
  userComment: r.userComment ?? r.UserComment ?? "",
  managerRating: Number(r.managerRating ?? r.ManagerRating ?? 0),
  managerComment: r.managerComment ?? r.ManagerComment ?? "",
  meetingRating: Number(r.meetingRating ?? r.MeetingRating ?? 0),
  meetingComment: r.meetingComment ?? r.MeetingComment ?? "",
  hasSelfRating: Boolean(r.hasSelfRating ?? r.HasSelfRating),
  hasManagerRating: Boolean(r.hasManagerRating ?? r.HasManagerRating),
});

const normalizeRetroReview = (r) => ({
  id: r.id ?? r.Id,
  appraisalRetrospectiveId: r.appraisalRetrospectiveId ?? r.AppraisalRetrospectiveId,
  retrospectiveName: r.retrospectiveName ?? r.RetrospectiveName ?? "",
  retrospectiveDescription: r.retrospectiveDescription ?? r.RetrospectiveDescription ?? "",
  sortOrder: Number(r.sortOrder ?? r.SortOrder ?? 0),
  userRating: Number(r.userRating ?? r.UserRating ?? 0),
  userComment: r.userComment ?? r.UserComment ?? "",
  managerRating: Number(r.managerRating ?? r.ManagerRating ?? 0),
  managerComment: r.managerComment ?? r.ManagerComment ?? "",
  meetingRating: Number(r.meetingRating ?? r.MeetingRating ?? 0),
  meetingComment: r.meetingComment ?? r.MeetingComment ?? "",
  hasSelfRating: Boolean(r.hasSelfRating ?? r.HasSelfRating),
  hasManagerRating: Boolean(r.hasManagerRating ?? r.HasManagerRating),
});

const normalizeDetail = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const reviews = raw.reviews ?? raw.Reviews ?? [];
  const retroReviews = raw.retroReviews ?? raw.RetroReviews ?? [];
  return {
    id: raw.id ?? raw.Id,
    userId: raw.userId ?? raw.UserId,
    userName: raw.userName ?? raw.UserName ?? "",
    managerId: raw.managerId ?? raw.ManagerId,
    managerName: raw.managerName ?? raw.ManagerName ?? "",
    companyAppraisalId: raw.companyAppraisalId ?? raw.CompanyAppraisalId,
    appraisalDate: raw.appraisalDate ?? raw.AppraisalDate,
    status: Number(raw.status ?? raw.Status ?? 0),
    statusName: raw.statusName ?? raw.StatusName ?? "",
    finalComments: raw.finalComments ?? raw.FinalComments ?? "",
    managerFinalComments: raw.managerFinalComments ?? raw.ManagerFinalComments ?? "",
    meetingFinalComments: raw.meetingFinalComments ?? raw.MeetingFinalComments ?? "",
    behaviorRatingMax: Number(
      raw.behaviourRatingMax ?? raw.BehaviourRatingMax ?? raw.behaviorRatingMax ?? raw.BehaviorRatingMax ?? 5
    ),
    retrospectiveRatingMax: Number(raw.retrospectiveRatingMax ?? raw.RetrospectiveRatingMax ?? 5),
    behaviorRatingLabels: normalizeRatingLabels(
      raw.behaviourRatingLabels ??
        raw.BehaviourRatingLabels ??
        raw.behaviorRatingLabels ??
        raw.BehaviorRatingLabels
    ),
    retrospectiveRatingLabels: normalizeRatingLabels(
      raw.retrospectiveRatingLabels ?? raw.RetrospectiveRatingLabels
    ),
    payIncreaseEnabled: Boolean(raw.payIncreaseEnabled ?? raw.PayIncreaseEnabled),
    isPayIncreased: raw.isPayIncreased ?? raw.IsPayIncreased,
    currentGrossSalary: raw.currentGrossSalary ?? raw.CurrentGrossSalary,
    salaryIncrementId: raw.salaryIncrementId ?? raw.SalaryIncrementId,
    recommendedNewGrossSalary: raw.recommendedNewGrossSalary ?? raw.RecommendedNewGrossSalary,
    recommendedEffectiveFrom: raw.recommendedEffectiveFrom ?? raw.RecommendedEffectiveFrom,
    payrollStructureName: raw.payrollStructureName ?? raw.PayrollStructureName,
    selfAssessmentHidden: Boolean(raw.selfAssessmentHidden ?? raw.SelfAssessmentHidden),
    retrospectivesAddedAfterSelfSubmit: Boolean(
      raw.retrospectivesAddedAfterSelfSubmit ?? raw.RetrospectivesAddedAfterSelfSubmit
    ),
    cycleLabel: raw.cycleLabel ?? raw.CycleLabel ?? "",
    reviews: (Array.isArray(reviews) ? reviews : []).map(normalizeReview),
    retroReviews: (Array.isArray(retroReviews) ? retroReviews : []).map(normalizeRetroReview),
  };
};

const AppraisalAssessmentPage = ({ mode = "self", embeddedAppraisalId = null, onClose = null }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id: routeId } = useParams();
  const { features } = useAppraisalFeatures();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [detail, setDetail] = useState(null);
  const [comments, setComments] = useState("");
  const [payRecommend, setPayRecommend] = useState(true);
  const [payNewGross, setPayNewGross] = useState("");
  const [payEffectiveFrom, setPayEffectiveFrom] = useState("");
  const [payReason, setPayReason] = useState("");
  const [paySaving, setPaySaving] = useState(false);
  const [helpKey, setHelpKey] = useState(null);
  const [myCycles, setMyCycles] = useState([]);
  const [selectedSelfId, setSelectedSelfId] = useState("");

  const assessmentId = useMemo(() => {
    if (mode !== "team") return null;
    if (embeddedAppraisalId) return embeddedAppraisalId;
    return (
      location.state?.userAppraisalId ||
      location.state?.id ||
      getAppraisalAssessmentId() ||
      routeId ||
      null
    );
  }, [mode, embeddedAppraisalId, location.state, routeId]);

  // Persist id + strip legacy /appraisal/assessment/:id from the URL.
  // Skip entirely when embedded — the parent owns the id and the URL must not change.
  useEffect(() => {
    if (mode !== "team" || embeddedAppraisalId) return;
    if (routeId) {
      setAppraisalAssessmentId(routeId);
      history.replace({
        pathname: "/appraisal/assessment",
        state: { userAppraisalId: routeId },
      });
      return;
    }
    if (assessmentId) setAppraisalAssessmentId(assessmentId);
  }, [mode, embeddedAppraisalId, routeId, assessmentId, history]);

  // Admins without a line manager should not use My Appraisal
  useEffect(() => {
    if (mode !== "self") return;
    if (!canAccessMyAppraisal()) {
      history.replace("/appraisal/overview");
    }
  }, [mode, history]);

  const applyDetail = (data) => {
    const normalized = normalizeDetail(data);
    setDetail(normalized);
    if (!normalized) return;
    if (normalized.status === APPRAISAL_STATUS.Pending) setComments(normalized.finalComments || "");
    else if (normalized.status === APPRAISAL_STATUS.SelfSubmitted) setComments(normalized.managerFinalComments || "");
    else setComments(normalized.meetingFinalComments || "");
    setPayRecommend(normalized.isPayIncreased !== false);
    setPayNewGross(
      normalized.recommendedNewGrossSalary != null
        ? String(normalized.recommendedNewGrossSalary)
        : normalized.currentGrossSalary != null
          ? String(normalized.currentGrossSalary)
          : ""
    );
    setPayEffectiveFrom(
      normalized.recommendedEffectiveFrom
        ? String(normalized.recommendedEffectiveFrom).slice(0, 10)
        : ""
    );
    setPayReason("");
  };

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      if (mode === "team") {
        if (!assessmentId) {
          setDetail(null);
          setError(
            t("app.appraisal.assessmentMissing", {
              defaultValue: "No appraisal selected. Open one from Overview or Team Appraisals.",
            })
          );
          return;
        }
        const data = await getAppraisalDetail(assessmentId);
        applyDetail(data);
      } else {
        const historyRows = await getMyAppraisalHistory();
        const list = (Array.isArray(historyRows) ? historyRows : []).map((r) => ({
          id: r.id ?? r.Id,
          appraisalDate: r.appraisalDate ?? r.AppraisalDate,
          status: Number(r.status ?? r.Status ?? 0),
          cycleLabel: r.cycleLabel ?? r.CycleLabel ?? "",
          isLatestCycle: Boolean(r.isLatestCycle ?? r.IsLatestCycle),
        }));
        setMyCycles(list);

        let targetId = selectedSelfId;
        if (!targetId || !list.some((r) => String(r.id) === String(targetId))) {
          const latest = list.find((r) => r.isLatestCycle);
          // Prefer the company latest cycle; fall back to newest appraisal date.
          const byDate = [...list].sort(
            (a, b) => new Date(b.appraisalDate) - new Date(a.appraisalDate)
          );
          targetId = latest?.id || byDate[0]?.id || "";
          setSelectedSelfId(targetId ? String(targetId) : "");
        }

        if (!targetId) {
          setDetail(null);
          return;
        }

        const data = await getAppraisalDetail(targetId);
        applyDetail(data);
      }
    } catch (e) {
      setDetail(null);
      setError(e?.message || "Failed to load appraisal");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [assessmentId, mode, selectedSelfId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const patchReviewRating = (behaviorId, value) => {
    setDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        reviews: (prev.reviews || []).map((r) =>
          r.appraisalBehaviorId === behaviorId ? { ...r, [ratingKey]: Number(value) } : r
        ),
      };
    });
  };

  const patchRetroRating = (retroId, value) => {
    setDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        retroReviews: (prev.retroReviews || []).map((r) =>
          r.appraisalRetrospectiveId === retroId ? { ...r, [ratingKey]: Number(value) } : r
        ),
      };
    });
  };

  const actor = useMemo(() => {
    const userId = getUserId();
    const roleId = getRoleId();
    const isOwner = detail?.userId && detail.userId === userId;
    const isManager = detail?.managerId && detail.managerId === userId;
    const isAdmin = roleId === 1 || roleId === 2;
    return { isOwner, isManager, isAdmin, roleId };
  }, [detail]);

  const stage = useMemo(() => {
    if (!detail) return null;
    const status = Number(detail.status);
    if (status === APPRAISAL_STATUS.Pending && actor.isOwner) return "self";
    if (status === APPRAISAL_STATUS.SelfSubmitted && (actor.isManager || actor.isAdmin)) return "manager";
    if (status === APPRAISAL_STATUS.ManagerSubmitted && (actor.isManager || actor.isAdmin)) return "meeting";
    return "readonly";
  }, [detail, actor]);

  const ratingKey = stage === "manager" ? "managerRating" : stage === "meeting" ? "meetingRating" : "userRating";
  const commentKey = stage === "manager" ? "managerComment" : stage === "meeting" ? "meetingComment" : "userComment";

  const onRate = async (review, value) => {
    if (!detail || stage === "readonly") return;
    setSaving(true);
    setError("");
    patchReviewRating(review.appraisalBehaviorId, value);
    try {
      const payload = {
        userAppraisalId: detail.id,
        appraisalBehaviourId: review.appraisalBehaviorId,
      };
      if (stage === "self") {
        payload.userRating = Number(value);
        payload.userComment = review.userComment || "";
      } else if (stage === "manager") {
        payload.managerRating = Number(value);
        payload.managerComment = review.managerComment || "";
      } else if (stage === "meeting") {
        payload.meetingRating = Number(value);
        payload.meetingComment = review.meetingComment || "";
      }
      await saveAppraisalReview(payload);
      await load({ silent: true });
    } catch (e) {
      setError(e?.message || "Failed to save rating");
      await load({ silent: true });
    } finally {
      setSaving(false);
    }
  };

  const onRetroRate = async (review, value) => {
    if (!detail || stage === "readonly") return;
    setSaving(true);
    setError("");
    patchRetroRating(review.appraisalRetrospectiveId, value);
    try {
      const payload = {
        userAppraisalId: detail.id,
        appraisalRetrospectiveId: review.appraisalRetrospectiveId,
      };
      if (stage === "self") {
        payload.userRating = Number(value);
        payload.userComment = review.userComment || "";
      } else if (stage === "manager") {
        payload.managerRating = Number(value);
        payload.managerComment = review.managerComment || "";
      } else if (stage === "meeting") {
        payload.meetingRating = Number(value);
        payload.meetingComment = review.meetingComment || "";
      }
      await saveAppraisalRetroReview(payload);
      await load({ silent: true });
    } catch (e) {
      setError(e?.message || "Failed to save retrospective rating");
      await load({ silent: true });
    } finally {
      setSaving(false);
    }
  };

  const onCommentBlur = async (review, rawText) => {
    if (!detail || stage === "readonly") return;
    const text = clampLen(rawText, APPRAISAL_LIMITS.comment);
    try {
      const payload = {
        userAppraisalId: detail.id,
        appraisalBehaviourId: review.appraisalBehaviorId,
      };
      if (stage === "self") {
        payload.userRating = review.userRating || 0;
        payload.userComment = text;
      } else if (stage === "manager") {
        payload.managerRating = review.managerRating || 0;
        payload.managerComment = text;
      } else if (stage === "meeting") {
        payload.meetingRating = review.meetingRating || 0;
        payload.meetingComment = text;
      }
      await saveAppraisalReview(payload);
    } catch (e) {
      setError(e?.message || "Failed to save comment");
    }
  };

  const onRetroCommentBlur = async (review, rawText) => {
    if (!detail || stage === "readonly") return;
    const text = clampLen(rawText, APPRAISAL_LIMITS.comment);
    try {
      const payload = {
        userAppraisalId: detail.id,
        appraisalRetrospectiveId: review.appraisalRetrospectiveId,
      };
      if (stage === "self") {
        payload.userRating = review.userRating || 0;
        payload.userComment = text;
      } else if (stage === "manager") {
        payload.managerRating = review.managerRating || 0;
        payload.managerComment = text;
      } else if (stage === "meeting") {
        payload.meetingRating = review.meetingRating || 0;
        payload.meetingComment = text;
      }
      await saveAppraisalRetroReview(payload);
    } catch (e) {
      setError(e?.message || "Failed to save retrospective comment");
    }
  };

  const onSubmit = async () => {
    if (!detail) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      if (stage === "self") {
        await submitSelfAppraisal({ userAppraisalId: detail.id, finalComments: comments });
      } else if (stage === "manager") {
        await submitManagerAppraisal({ userAppraisalId: detail.id, managerFinalComments: comments });
      } else if (stage === "meeting") {
        await submitMeetingAppraisal({ userAppraisalId: detail.id, meetingFinalComments: comments });
      }
      setMessage("Submitted successfully.");
      await load({ silent: true });
      if (mode === "team") {
        if (embeddedAppraisalId) {
          if (onClose) onClose();
        } else {
          clearAppraisalAssessmentId();
          history.push("/appraisal/team");
        }
      }
    } catch (e) {
      setError(e?.message || "Submit failed");
    } finally {
      setSaving(false);
    }
  };

  // Pay increase only when Admin enabled it in Appraisal Settings.
  const payIncreaseFeatureOn =
    Boolean(features?.isPayIncrease) && Boolean(detail?.payIncreaseEnabled);

  const canRecommendPay =
    payIncreaseFeatureOn &&
    (actor.isManager || actor.isAdmin) &&
    (Number(detail?.status) === APPRAISAL_STATUS.ManagerSubmitted ||
      Number(detail?.status) === APPRAISAL_STATUS.MeetingConfirmed);

  // On My Appraisal (self), hide the manager-oriented card unless a decision exists
  // or the viewer can recommend. Team assessment shows it whenever the feature is on.
  const showPayIncrease =
    payIncreaseFeatureOn &&
    (mode === "team" || canRecommendPay || detail?.isPayIncreased != null);

  const onRecommendPay = async () => {
    if (!detail || !payIncreaseFeatureOn) return;
    setPaySaving(true);
    setError("");
    setMessage("");
    try {
      await recommendAppraisalPayIncrease({
        userAppraisalId: detail.id,
        recommendIncrease: payRecommend,
        newGrossSalary: payRecommend ? Number(payNewGross) : null,
        effectiveFrom: payRecommend && payEffectiveFrom ? payEffectiveFrom : null,
        reason: payReason || null,
      });
      setMessage(
        payRecommend
          ? "Pay increase recommended. A pending salary increment was created for payroll admin to activate."
          : "Marked as no pay increase."
      );
      await load({ silent: true });
    } catch (e) {
      setError(e?.message || "Failed to save pay increase recommendation");
    } finally {
      setPaySaving(false);
    }
  };

  const max = detail?.behaviorRatingMax || 5;
  const retroMax = detail?.retrospectiveRatingMax || 5;
  const behaviorGuide = buildRatingGuide(max, detail?.behaviorRatingLabels || []);
  const retroGuide = buildRatingGuide(retroMax, detail?.retrospectiveRatingLabels || []);
  const title =
    mode === "team"
      ? t("app.appraisal.assessmentTitle", { defaultValue: "Staff Assessment" })
      : t("app.appraisal.selfTitle", { defaultValue: "My Appraisal" });

  const selectedCycle = useMemo(
    () => myCycles.find((c) => String(c.id) === String(selectedSelfId)) || null,
    [myCycles, selectedSelfId]
  );

  const ratingProgress = useMemo(() => {
    if (!detail) return { done: 0, total: 0 };
    const key =
      stage === "manager" ? "managerRating" : stage === "meeting" ? "meetingRating" : "userRating";
    const items = [...(detail.reviews || []), ...(detail.retroReviews || [])];
    const done = items.filter((r) => Number(r[key]) > 0).length;
    return { done, total: items.length };
  }, [detail, stage]);

  const description =
    mode === "self"
      ? t("app.appraisal.selfSubtitle", {
          defaultValue: "Rate your behaviors for this cycle, add comments, then submit for your manager.",
        })
      : detail
        ? `${detail.userName || ""}${detail.cycleLabel ? ` · ${detail.cycleLabel}` : ""} · ${appraisalStatusLabel(detail.status)}`
        : "";

  const hasFramework =
    (detail?.reviews?.length ?? 0) > 0 || (detail?.retroReviews?.length ?? 0) > 0;

  const renderRatingButtons = (currentValue, maxScore, labels, disabled, onPick) => (
    <div className="appraisal-rating-row" role="group" aria-label={t("app.appraisal.ratingPick", { defaultValue: "Select a rating" })}>
      {Array.from({ length: maxScore }, (_, i) => i + 1).map((n) => {
        const label = getRatingLabel(n, maxScore, labels);
        return (
          <button
            key={n}
            type="button"
            className={`appraisal-rating-btn ${Number(currentValue) === n ? "active" : ""}`}
            disabled={disabled}
            title={`${n} — ${label}`}
            aria-label={`${n}: ${label}`}
            onClick={() => onPick(n)}
          >
            <span className="appraisal-rating-btn__num">{n}</span>
            <span className="appraisal-rating-btn__label">{label}</span>
          </button>
        );
      })}
    </div>
  );

  const renderRatingLegend = (guide, helpKeyName) => (
    <div className="appraisal-rating-legend-wrap">
      <div className="appraisal-rating-legend-head">
        <span className="appraisal-rating-legend-title">
          {t("app.appraisal.ratingGuideTitle", { defaultValue: "What the numbers mean" })}
        </span>
        <button
          type="button"
          className="appraisal-framework-info-btn appraisal-framework-info-btn--compact"
          onClick={() => setHelpKey(helpKeyName)}
          title={t("app.appraisal.help.openTitle", { defaultValue: "What is this?" })}
        >
          <i className="bi bi-info-circle" aria-hidden />
          <span>{t("app.appraisal.help.learn", { defaultValue: "What is this?" })}</span>
        </button>
      </div>
      <ul className="appraisal-rating-legend">
        {guide.map(({ n, label }) => (
          <li key={n}>
            <strong>{n}</strong>
            <span>{label}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className={`appraisal-page appraisal-assessment-page${mode === "self" ? " appraisal-self-page" : ""}`}>
      <CommonPageHeader
        icon={<i className="bi bi-clipboard-check" />}
        title={title}
        description={description}
        onBack={
          mode === "team"
            ? () => {
                if (embeddedAppraisalId) {
                  if (onClose) onClose();
                  return;
                }
                clearAppraisalAssessmentId();
                history.push(getRoleId() <= 2 ? "/appraisal/overview" : "/appraisal/team");
              }
            : undefined
        }
      />

      {mode === "self" && myCycles.length > 0 ? (
        <section className="appraisal-self-cycle">
          <div className="appraisal-self-cycle__head">
            <div className="appraisal-self-cycle__icon" aria-hidden>
              <i className="bi bi-calendar3" />
            </div>
            <div className="appraisal-self-cycle__copy">
              <strong>
                {t("app.appraisal.myCycleFilter", { defaultValue: "My appraisal cycle" })}
              </strong>
              <p>
                {t("app.appraisal.myCycleHint", {
                  defaultValue:
                    "The latest cycle is selected by default. Switch here only if you need an earlier cycle.",
                })}
              </p>
            </div>
            {detail ? (
              <span className={`appraisal-status-pill appraisal-status-pill--${Number(detail.status) || 0}`}>
                {appraisalStatusLabel(detail.status)}
              </span>
            ) : null}
          </div>

          <div className="appraisal-self-cycle__control">
            <label htmlFor="appraisal-self-cycle" className="appraisal-self-cycle__label">
              {t("app.appraisal.cycleFilter", { defaultValue: "Appraisal cycle" })}
            </label>
            <div className="appraisal-self-cycle__select-wrap">
              <i className="bi bi-funnel" aria-hidden />
              <select
                id="appraisal-self-cycle"
                value={selectedSelfId}
                onChange={(e) => setSelectedSelfId(e.target.value)}
              >
                {myCycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.cycleLabel ||
                      (c.appraisalDate
                        ? new Date(c.appraisalDate).toLocaleDateString()
                        : c.id)}
                    {" · "}
                    {appraisalStatusLabel(c.status)}
                    {c.status !== APPRAISAL_STATUS.MeetingConfirmed
                      ? t("app.appraisal.incompleteSuffix", {
                          defaultValue: " · incomplete",
                        })
                      : ""}
                    {c.isLatestCycle
                      ? t("app.appraisal.cycleLatestSuffix", {
                          defaultValue: " (latest)",
                        })
                      : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="appraisal-self-cycle__footer">
            <div className="appraisal-self-cycle__meta">
              {selectedCycle?.isLatestCycle ? (
                <span className="appraisal-cycle-badge__latest">
                  {t("app.appraisal.latestShort", { defaultValue: "Latest" })}
                </span>
              ) : null}
              {Number(detail?.status) === APPRAISAL_STATUS.Pending ? (
                <span className="appraisal-self-cycle__hint">
                  {t("app.appraisal.completeThisCycleHint", {
                    defaultValue:
                      "Complete Behaviors (and Retrospective if enabled), then submit self assessment.",
                  })}
                </span>
              ) : null}
            </div>
            {ratingProgress.total > 0 && stage !== "readonly" ? (
              <div className="appraisal-self-progress" aria-live="polite">
                <div className="appraisal-self-progress__label">
                  {t("app.appraisal.ratingProgress", {
                    defaultValue: "{{done}} of {{total}} rated",
                    done: ratingProgress.done,
                    total: ratingProgress.total,
                  })}
                </div>
                <div className="appraisal-self-progress__track">
                  <div
                    className="appraisal-self-progress__fill"
                    style={{
                      width: `${Math.round(
                        (ratingProgress.done / Math.max(ratingProgress.total, 1)) * 100
                      )}%`,
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {loading && !detail ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {message ? <div className="appraisal-success">{message}</div> : null}
      {!loading && !detail && !error ? (
        <div className="appraisal-team-empty">
          <i className="bi bi-clipboard-check" aria-hidden />
          <p>
            {t("app.appraisal.noCurrent", {
              defaultValue: "No active appraisal. Ask your admin to start a cycle.",
            })}
          </p>
        </div>
      ) : null}

      {detail && (
        <>
          {!hasFramework ? (
            <div className="appraisal-muted">
              {t("app.appraisal.noFramework", {
                defaultValue:
                  "No behaviors or retrospectives are available for this employee yet. Add them under Values & Behaviors / Retrospectives, then reopen this assessment.",
              })}
            </div>
          ) : null}

          {(detail.reviews || []).length > 0 && (
            <section className="appraisal-settings-panel appraisal-assessment-section">
              <div className="appraisal-settings-panel__head">
                <AppraisalSectionHeader
                  icon="bi-list-check"
                  title={t("app.appraisal.behaviorsSection", { defaultValue: "Behaviors" })}
                  helpKey="behaviors"
                  onOpenHelp={setHelpKey}
                  tone="behaviors"
                />
                {stage === "manager" ? (
                  <p className="appraisal-framework-hint">
                    {t("app.appraisal.blindManagerHint", {
                      defaultValue:
                        "Self ratings and comments are hidden while you complete the manager assessment. They become visible at the meeting stage. Behaviors already rated by the employee stay completed — enter your manager scores below.",
                    })}
                  </p>
                ) : (
                  <p className="appraisal-framework-hint">
                    {t("app.appraisal.selfBehaviorsHint", {
                      defaultValue:
                        "Select a score for each behavior. Your choice saves automatically — the form stays open so you can continue.",
                    })}
                  </p>
                )}
              </div>
              {stage === "manager" || stage === "meeting" ? (
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.behaviorProgress", {
                    defaultValue: "Behaviors progress: {{done}} / {{total}} with your rating",
                    done: (detail.reviews || []).filter((r) =>
                      stage === "meeting"
                        ? Number(r.meetingRating) > 0
                        : Number(r.managerRating) > 0
                    ).length,
                    total: (detail.reviews || []).length,
                  })}
                </p>
              ) : null}
              {renderRatingLegend(behaviorGuide, "ratingScale")}
              <div className="appraisal-reviews">
                {(detail.reviews || []).map((r) => (
                  <article
                    key={r.appraisalBehaviorId}
                    className={`appraisal-review-card${Number(r[ratingKey]) > 0 ? " is-rated" : ""}`}
                  >
                    <div className="appraisal-review-head">
                      <span className="appraisal-chip">{r.valueName || "Value"}</span>
                      <h4>{r.behaviorStatement}</h4>
                      {r.behaviorIntroduction ? <p>{r.behaviorIntroduction}</p> : null}
                      {r.behaviorHelpText ? (
                        <p className="appraisal-behavior-help">{r.behaviorHelpText}</p>
                      ) : null}
                      {stage === "manager" && detail.selfAssessmentHidden ? (
                        <p className="appraisal-behavior-help">
                          {r.hasSelfRating
                            ? t("app.appraisal.selfCompletedHidden", {
                                defaultValue: "Self rating: completed (hidden until meeting)",
                              })
                            : t("app.appraisal.selfNotRated", {
                                defaultValue: "Self rating: not provided",
                              })}
                        </p>
                      ) : null}
                    </div>
                    {stage === "meeting" ? (
                      <div className="appraisal-readonly-scores appraisal-prior-scores">
                        <span>Self: {r.userRating || "—"}</span>
                        <span>Manager: {r.managerRating || "—"}</span>
                      </div>
                    ) : null}
                    {renderRatingButtons(
                      r[ratingKey],
                      max,
                      detail.behaviorRatingLabels || [],
                      stage === "readonly" || saving,
                      (n) => onRate(r, n)
                    )}
                    <label className="appraisal-field appraisal-review-comment">
                      {t("app.appraisal.comment", { defaultValue: "Comment" })}
                      <textarea
                        className="appraisal-comment"
                        maxLength={APPRAISAL_LIMITS.comment}
                        placeholder={t("app.appraisal.commentPlaceholder", {
                          defaultValue: "Optional notes for this behavior…",
                        })}
                        defaultValue={r[commentKey] || ""}
                        disabled={stage === "readonly"}
                        onBlur={(e) => onCommentBlur(r, e.target.value)}
                      />
                    </label>
                    {stage === "readonly" && (
                      <div className="appraisal-readonly-scores">
                        <span>Self: {r.userRating || "—"}</span>
                        <span>Manager: {r.managerRating || "—"}</span>
                        <span>Meeting: {r.meetingRating || "—"}</span>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}

          {(detail.retroReviews || []).length > 0 && (
            <section className="appraisal-settings-panel appraisal-assessment-section">
              <div className="appraisal-settings-panel__head">
                <AppraisalSectionHeader
                  icon="bi-arrow-counterclockwise"
                  title={t("app.appraisal.retroSection", { defaultValue: "Retrospective" })}
                  helpKey="retrospective"
                  onOpenHelp={setHelpKey}
                  tone="retrospective"
                />
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.selfRetroHint", {
                    defaultValue: "Reflect on the period — scores save as you select them.",
                  })}
                </p>
              </div>
              {detail.retrospectivesAddedAfterSelfSubmit ? (
                <div className="appraisal-feature-callout" style={{ marginBottom: "0.75rem" }}>
                  <i className="bi bi-info-circle" aria-hidden />
                  <div>
                    <strong>
                      {t("app.appraisal.retroMidCycleTitle", {
                        defaultValue: "Retrospective (separate from Behaviors)",
                      })}
                    </strong>
                    <p>
                      {t("app.appraisal.retroMidCycleBody", {
                        defaultValue:
                          "These reflection questions were added after self submission. They are not Behaviors — your behavior ratings above are unchanged. Rate these Retrospective items for the current stage.",
                      })}
                    </p>
                  </div>
                </div>
              ) : null}
              {stage === "manager" ? (
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.blindManagerHint", {
                    defaultValue:
                      "Self ratings and comments are hidden while you complete the manager assessment. They become visible at the meeting stage.",
                  })}
                </p>
              ) : null}
              {renderRatingLegend(retroGuide, "ratingScale")}
              <div className="appraisal-reviews">
                {(detail.retroReviews || []).map((r) => (
                  <article
                    key={r.appraisalRetrospectiveId}
                    className={`appraisal-review-card${Number(r[ratingKey]) > 0 ? " is-rated" : ""}`}
                  >
                    <div className="appraisal-review-head">
                      <span className="appraisal-chip">Retrospective</span>
                      <h4>{r.retrospectiveName}</h4>
                      {r.retrospectiveDescription ? <p>{r.retrospectiveDescription}</p> : null}
                      {stage === "manager" && detail.selfAssessmentHidden ? (
                        <p className="appraisal-behavior-help">
                          {r.hasSelfRating
                            ? t("app.appraisal.selfCompletedHidden", {
                                defaultValue: "Self rating: completed (hidden until meeting)",
                              })
                            : t("app.appraisal.selfNotRated", {
                                defaultValue: "Self rating: not provided",
                              })}
                        </p>
                      ) : null}
                    </div>
                    {stage === "meeting" ? (
                      <div className="appraisal-readonly-scores appraisal-prior-scores">
                        <span>Self: {r.userRating || "—"}</span>
                        <span>Manager: {r.managerRating || "—"}</span>
                      </div>
                    ) : null}
                    {renderRatingButtons(
                      r[ratingKey],
                      retroMax,
                      detail.retrospectiveRatingLabels || [],
                      stage === "readonly" || saving,
                      (n) => onRetroRate(r, n)
                    )}
                    <label className="appraisal-field appraisal-review-comment">
                      {t("app.appraisal.comment", { defaultValue: "Comment" })}
                      <textarea
                        className="appraisal-comment"
                        maxLength={APPRAISAL_LIMITS.comment}
                        placeholder={t("app.appraisal.commentPlaceholder", {
                          defaultValue: "Optional notes for this item…",
                        })}
                        defaultValue={r[commentKey] || ""}
                        disabled={stage === "readonly"}
                        onBlur={(e) => onRetroCommentBlur(r, e.target.value)}
                      />
                    </label>
                    {stage === "readonly" && (
                      <div className="appraisal-readonly-scores">
                        <span>Self: {r.userRating || "—"}</span>
                        <span>Manager: {r.managerRating || "—"}</span>
                        <span>Meeting: {r.meetingRating || "—"}</span>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}

          {stage !== "readonly" && (
            <section className="appraisal-settings-panel appraisal-assessment-section">
              <div className="appraisal-settings-panel__head">
                <h3 className="appraisal-assessment-submit-title">
                  {t("app.appraisal.finalComments", { defaultValue: "Final comments" })}
                </h3>
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.finalCommentsHint", {
                    defaultValue: "Add overall comments, then submit when all items are rated.",
                  })}
                </p>
              </div>
              <textarea
                className="appraisal-comment"
                maxLength={APPRAISAL_LIMITS.longText}
                value={comments}
                onChange={(e) =>
                  setComments(clampLen(e.target.value, APPRAISAL_LIMITS.longText))
                }
                rows={4}
                placeholder={t("app.appraisal.finalCommentsPlaceholder", {
                  defaultValue: "Overall reflection for this cycle…",
                })}
              />
              <AppraisalCharCount value={comments} max={APPRAISAL_LIMITS.longText} />
              <div className="appraisal-framework-form__actions" style={{ marginTop: "0.85rem" }}>
                <button
                  type="button"
                  className="appraisal-btn primary"
                  disabled={saving}
                  onClick={onSubmit}
                >
                  {saving
                    ? t("app.common.saving", { defaultValue: "Saving…" })
                    : stage === "self"
                      ? t("app.appraisal.submitSelf", {
                          defaultValue: "Submit self assessment",
                        })
                      : stage === "manager"
                        ? t("app.appraisal.submitManager", {
                            defaultValue: "Submit manager assessment",
                          })
                        : t("app.appraisal.confirmMeeting", {
                            defaultValue: "Confirm appraisal meeting",
                          })}
                </button>
              </div>
            </section>
          )}

          {showPayIncrease ? (
            <section className="appraisal-card">
              <AppraisalSectionHeader
                icon="bi-cash-coin"
                title={t("app.appraisal.payIncreaseTitle", { defaultValue: "Pay increase" })}
                helpKey="payIncrease"
                onOpenHelp={setHelpKey}
                tone="payIncrease"
              />
              <p className="appraisal-framework-hint">
                {canRecommendPay
                  ? t("app.appraisal.payIncreaseHint", {
                      defaultValue:
                        "Recommend a raise from this appraisal. It creates a Pending Salary Increment — payroll must activate it.",
                    })
                  : t("app.appraisal.payIncreaseSelfHint", {
                      defaultValue:
                        "Pay recommendation from your manager for this appraisal cycle (if any).",
                    })}
              </p>
              {canRecommendPay ? (
                <p className="appraisal-muted" style={{ marginTop: 0 }}>
                  Linked to the employee&apos;s current payroll structure
                  {detail.payrollStructureName ? ` (${detail.payrollStructureName})` : ""}.
                  Recommendations create a <strong>Pending</strong> salary increment; payroll admin
                  activates it under Payroll → Salary increments.
                </p>
              ) : null}
              <div className="appraisal-stats" style={{ marginBottom: "1rem" }}>
                <div>
                  <span>{detail.currentGrossSalary != null ? detail.currentGrossSalary : "—"}</span>
                  Current gross
                </div>
                <div>
                  <span>
                    {detail.isPayIncreased == null ? "—" : detail.isPayIncreased ? "Recommended" : "No"}
                  </span>
                  Decision
                </div>
                <div>
                  <span>{detail.recommendedNewGrossSalary != null ? detail.recommendedNewGrossSalary : "—"}</span>
                  Recommended gross
                </div>
              </div>

              {canRecommendPay ? (
                <>
                  <div className="appraisal-toggles" style={{ marginBottom: "0.75rem" }}>
                    <label className="appraisal-toggle">
                      <input
                        type="radio"
                        name="payRecommend"
                        checked={payRecommend}
                        onChange={() => setPayRecommend(true)}
                      />
                      Recommend increase
                    </label>
                    <label className="appraisal-toggle">
                      <input
                        type="radio"
                        name="payRecommend"
                        checked={!payRecommend}
                        onChange={() => setPayRecommend(false)}
                      />
                      No increase
                    </label>
                  </div>
                  {payRecommend && (
                    <div className="appraisal-grid-3">
                      <label className="appraisal-field">
                        New gross salary
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={payNewGross}
                          onChange={(e) => setPayNewGross(e.target.value)}
                        />
                      </label>
                      <label className="appraisal-field">
                        Effective from
                        <input
                          type="date"
                          value={payEffectiveFrom}
                          onChange={(e) => setPayEffectiveFrom(e.target.value)}
                        />
                      </label>
                      <label className="appraisal-field">
                        Reason (optional)
                        <input
                          maxLength={APPRAISAL_LIMITS.description}
                          value={payReason}
                          onChange={(e) =>
                            setPayReason(clampLen(e.target.value, APPRAISAL_LIMITS.description))
                          }
                        />
                        <AppraisalCharCount value={payReason} max={APPRAISAL_LIMITS.description} />
                      </label>
                    </div>
                  )}
                  <div className="appraisal-actions">
                    <button
                      type="button"
                      className="appraisal-btn primary"
                      disabled={paySaving}
                      onClick={onRecommendPay}
                    >
                      {paySaving ? "Saving…" : "Save pay recommendation"}
                    </button>
                  </div>
                </>
              ) : null}
            </section>
          ) : null}
        </>
      )}

      <AppraisalHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AppraisalAssessmentPage;
